import React, { useEffect, useMemo, useState } from "react";
import Card from "../ui/Card";
import Table from "../ui/Table";
import { useAuth } from "../context/AuthContext";
import {
  checkinCollectionPath,
  createCheckin,
  fetchRecentCheckins,
  fetchUserCheckinRange,
  resolveCheckinOrgId,
} from "../utils/checkins";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const MOODS = [
  { value: 1, label: "Awful" },
  { value: 2, label: "Bad" },
  { value: 3, label: "Okay" },
  { value: 4, label: "Good" },
  { value: 5, label: "Great" },
];

const MOOD_TAGS = {
  Great: [
    "Happy", "Joyful", "Grateful", "Excited", "Amazed", "Passionate",
    "Confident", "Brave", "Proud", "Hopeful", "Satisfied", "Relieved",
    "Amused", "Content", "Calm", "Peaceful", "Surprised",
  ],
  Good: [
    "Happy", "Joyful", "Grateful", "Content", "Calm", "Peaceful",
    "Confident", "Excited", "Hopeful", "Satisfied", "Relieved",
    "Amused", "Surprised", "Brave", "Proud", "Passionate", "Amazed",
  ],
  Okay: [
    "Content", "Calm", "Peaceful", "Satisfied", "Indifferent", "Drained",
  ],
  Bad: [
    "Anxious", "Stressed", "Worried", "Annoyed", "Disappointed", "Jealous",
    "Lonely", "Hopeless", "Discouraged", "Embarrassed", "Drained", "Sad",
    "Surprised", "Irritated", "Scared", "Guilty", "Ashamed", "Frustrated",
    "Disgusted", "Angry", "Overwhelmed",
  ],
  Awful: [
    "Angry", "Disgusted", "Frustrated", "Anxious", "Stressed", "Overwhelmed",
    "Annoyed", "Disappointed", "Lonely", "Guilty", "Ashamed", "Sad",
    "Irritated", "Worried", "Jealous", "Embarrassed", "Scared",
    "Hopeless", "Drained", "Discouraged",
  ],
};

const IMPACT_TAGS = [
  "Health", "Fitness", "Self‑Care", "Hobbies", "Identity", "Spirituality",
  "Community", "Family", "Friends", "Partner", "Dating", "Tasks",
  "Work", "Education", "Travel", "Weather", "Current Events", "Money",
];

export default function CheckInPage() {
  const { user, activeOrgId, effectiveRole } = useAuth();
  const [mood, setMood] = useState(3);
  const [moodTags, setMoodTags] = useState([]);
  const [impactTags, setImpactTags] = useState([]);
  const [status, setStatus] = useState("");
  const [sending, setSending] = useState(false);
  const [recent, setRecent] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [weekRows, setWeekRows] = useState([]);

  const orgId = resolveCheckinOrgId(user, activeOrgId, effectiveRole || user?.role || "member");
  const moodLabel = useMemo(() => MOODS.find((m) => m.value === mood)?.label || "Okay", [mood]);
  const checkinPath = checkinCollectionPath(orgId);

  const weekRange = useMemo(() => {
    const now = new Date();
    const base = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const day = base.getDay();
    const mondayOffset = (day + 6) % 7;
    const start = new Date(base);
    start.setDate(base.getDate() - mondayOffset - weekOffset * 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start, end };
  }, [weekOffset]);

  const weekLabel = useMemo(() => {
    const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", day: "2-digit" });
    return `${fmt(weekRange.start)} - ${fmt(weekRange.end)}`;
  }, [weekRange]);

  useEffect(() => {
    if (!user?.uid) return;
    fetchRecentCheckins(orgId, { max: 5, userId: user.uid })
      .then((rows) => {
        setRecent(rows);
        try {
          // eslint-disable-next-line no-console
          console.info("[checkins] checkInPathEquality", {
            writePath: checkinPath,
            recentReadPath: checkinPath,
            orgId,
            userIdPresent: Boolean(user?.uid),
            count: Array.isArray(rows) ? rows.length : 0,
          });
        } catch {}
      })
      .catch((error) => {
        setRecent([]);
        try {
          // eslint-disable-next-line no-console
          console.warn("[checkins] checkInRecentReadFailed", {
            path: checkinPath,
            orgId,
            userIdPresent: Boolean(user?.uid),
            code: error?.code || "",
            message: error?.message || "",
          });
        } catch {}
        setStatus("Unable to load previous check-ins right now.");
      });
  }, [user, orgId, checkinPath]);

  useEffect(() => {
    if (!user?.uid) return;
    const startMs = new Date(weekRange.start).setHours(0, 0, 0, 0);
    const endMs = new Date(weekRange.end).setHours(23, 59, 59, 999);
    fetchUserCheckinRange(orgId, user.uid, startMs, endMs)
      .then(setWeekRows)
      .catch((error) => {
        setWeekRows([]);
        try {
          // eslint-disable-next-line no-console
          console.warn("[checkins] checkInWeekReadFailed", {
            path: checkinPath,
            orgId,
            userIdPresent: Boolean(user?.uid),
            code: error?.code || "",
            message: error?.message || "",
          });
        } catch {}
      });
  }, [user, orgId, weekRange, checkinPath]);

  const moodSeries = useMemo(() => {
    const rows = weekRows.slice().sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    const byDay = new Map();
    rows.forEach((row) => {
      const key = new Date(row.createdAt || Date.now()).toLocaleDateString(undefined, { weekday: "short" });
      byDay.set(key, row.mood || 3);
    });
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return days.map((d) => ({ day: d, mood: byDay.get(d) || 0 }));
  }, [weekRows]);

  const influenceTags = useMemo(() => {
    const positive = new Map();
    const negative = new Map();
    weekRows.forEach((row) => {
      const tags = Array.isArray(row.impactTags) ? row.impactTags : [];
      const moodValue = Number(row.mood || 3);
      const bucket = moodValue >= 4 ? positive : moodValue <= 2 ? negative : null;
      if (!bucket) return;
      tags.forEach((tag) => {
        bucket.set(tag, (bucket.get(tag) || 0) + 1);
      });
    });
    const top = (map) =>
      Array.from(map.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([tag]) => tag);
    return { positive: top(positive), negative: top(negative) };
  }, [weekRows]);

  const toggleMoodTag = (tag) => {
    setMoodTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };
  const toggleImpactTag = (tag) => {
    setImpactTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const submit = async () => {
    if (!user?.uid) return;
    setSending(true);
    setStatus("Saving your check-in...");
    try {
      // eslint-disable-next-line no-console
      console.info("[checkins] checkInSubmitStarted", {
        path: checkinPath,
        orgId,
        userIdPresent: Boolean(user?.uid),
      });
      const entry = await createCheckin(user, orgId, {
        mood,
        moodLabel,
        tags: [...moodTags, ...impactTags],
        moodTags,
        impactTags,
      });
      if (!entry) {
        throw new Error("Missing authenticated user or organisation for check-in.");
      }
      if (!entry.id) {
        throw new Error("Firestore check-in write returned no document id.");
      }
      const readbackRows = await fetchRecentCheckins(orgId, { max: 5, userId: user.uid });
      const readbackEntry = readbackRows.find((row) => String(row.id) === String(entry.id));
      if (!readbackEntry?.id) {
        throw new Error("Firestore check-in write succeeded but same-scope readback did not find the document.");
      }
      // eslint-disable-next-line no-console
      console.info("[checkins] checkInSubmitSucceeded", {
        path: checkinPath,
        orgId,
        userIdPresent: Boolean(user?.uid),
        firestoreDocId: entry.id,
        addDocSucceeded: true,
        sameScopeReadbackSucceeded: true,
      });
      setStatus("Saved. Thank you for checking in.");
      setMoodTags([]);
      setImpactTags([]);
      setRecent(readbackRows);
      const startMs = new Date(weekRange.start).setHours(0, 0, 0, 0);
      const endMs = new Date(weekRange.end).setHours(23, 59, 59, 999);
      if (entry.createdAt >= startMs && entry.createdAt <= endMs) {
        setWeekRows((prev) => [entry, ...prev].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("[checkins] checkInSubmitFailed", {
        path: checkinPath,
        orgId,
        userIdPresent: Boolean(user?.uid),
        code: error?.code || "",
        message: error?.message || "",
      });
      setStatus("Unable to save your check-in. Please try again, or refresh and sign in again if it keeps happening.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="nia-page checkin-page">
      <Card className="nia-hero">
        <div className="nia-header">
          <div>
            <h2>Daily Check‑in</h2>
            <p>Share how you’re feeling today. This helps your coach support you better.</p>
          </div>
        </div>
      </Card>

      <div className="nia-grid nia-grid--2 checkin-grid">
        <Card title="How are you feeling?">
          <div className="checkin-mood">
            <div className="checkin-mood-label">{moodLabel}</div>
            <input
              className="checkin-slider"
              type="range"
              min={1}
              max={5}
              step={1}
              value={mood}
              onChange={(e) => setMood(Number(e.target.value))}
            />
            <div className="checkin-mood-scale">
              {MOODS.map((m) => (
                <span key={m.value} className={m.value === mood ? "active" : ""}>{m.label}</span>
              ))}
            </div>
          </div>
          <div className="checkin-tags">
            {(MOOD_TAGS[moodLabel] || []).map((tag) => (
              <button
                key={tag}
                type="button"
                className={`checkin-tag${moodTags.includes(tag) ? " active" : ""}`}
                onClick={() => toggleMoodTag(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
        </Card>

        <Card title="What’s having the biggest impact?">
          <div className="checkin-tags checkin-tags-impact">
            {IMPACT_TAGS.map((tag) => (
              <button
                key={tag}
                type="button"
                className={`checkin-tag${impactTags.includes(tag) ? " active" : ""}`}
                onClick={() => toggleImpactTag(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
          {status && <div className="nia-muted" style={{ marginTop: 10 }}>{status}</div>}
          <div className="checkin-actions">
            <button className="btn btn-primary" disabled={sending} onClick={submit}>
              Complete Check‑in
            </button>
          </div>
        </Card>
      </div>

      <Card title="Mood Analysis">
        <div className="checkin-week">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setWeekOffset((v) => v + 1)}
          >
            ‹
          </button>
          <div className="checkin-week-label">{weekLabel}</div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setWeekOffset((v) => Math.max(0, v - 1))}
            disabled={weekOffset === 0}
          >
            ›
          </button>
        </div>
        <div style={{ height: 200 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={10} minHeight={10} debounce={120}>
            <AreaChart data={moodSeries}>
              <XAxis dataKey="day" stroke="var(--ui-muted)" tick={{ fill: "var(--ui-muted)", fontSize: 12 }} />
              <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} stroke="var(--ui-muted)" tick={{ fill: "var(--ui-muted)", fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  background: "var(--ui-surface)",
                  border: "1px solid var(--ui-border)",
                  color: "var(--ui-text)",
                }}
              />
              <Area type="monotone" dataKey="mood" stroke="var(--ui-accent)" fill="var(--ui-accent)" fillOpacity={0.18} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="checkin-influences">
          <div className="checkin-influences-title">Mood influences</div>
          {influenceTags.positive.length > 0 && (
            <>
              <div className="nia-subtitle" style={{ marginTop: 8 }}>Positive influences</div>
              <div className="checkin-tags">
                {influenceTags.positive.map((tag) => (
                  <span key={tag} className="checkin-tag active">{tag}</span>
                ))}
              </div>
            </>
          )}
          {influenceTags.negative.length > 0 && (
            <>
              <div className="nia-subtitle" style={{ marginTop: 12 }}>Negative influences</div>
              <div className="checkin-tags">
                {influenceTags.negative.map((tag) => (
                  <span key={tag} className="checkin-tag">{tag}</span>
                ))}
              </div>
            </>
          )}
          {influenceTags.positive.length === 0 && influenceTags.negative.length === 0 && (
            <div className="nia-muted" style={{ marginTop: 8 }}>No mood influences logged for this week yet.</div>
          )}
        </div>
      </Card>

      <Card title="Your recent check‑ins">
        <Table
          headers={["Date", "Mood", "Tags"]}
          rows={
            recent.length
              ? recent.map((r) => [
                  new Date(r.createdAt).toLocaleDateString(),
                  r.moodLabel || "Okay",
                  (r.tags || []).slice(0, 4).join(", ") || "-",
                ])
              : [["-", "-", "No check-ins yet."]]
          }
        />
      </Card>
    </div>
  );
}
